import {formatMoney} from '@shopify/theme-currency';

import FetchError from '../util/fetch-error';
import slideDown from '../util/slide-down';
import slideUp from '../util/slide-up';

import {CartNotes} from './cart-notes';
import {ShippingCalculator} from './cart-shipping';
import {CartShippingMessage} from './cart-shipping-message';
import {initQtySection} from './quantity-selector';

const selectors = {
  drawer: '[data-drawer="drawer-cart"]',
  shipping: '[data-shipping-estimate-form]',
  loader: '[data-cart-loading]',
  form: '[data-cart-form]',
  emptystate: '[data-cart-empty]',
  progress: '[data-cart-progress]',
  items: '[data-line-items]',
  subtotal: '[data-cart-subtotal]',
  bottom: '[data-cart-bottom]',
  quantity: '[data-quantity-selector]',
  errors: '[data-form-errors]',
  item: '[data-cart-item]',
  finalPrice: '[data-cart-final]',
  key: 'data-update-cart',
  remove: 'data-remove-key',
  pageUpsellWrapper: '[data-cart-page-upsell-wrapper]',
  cartPage: '[data-section-type="cart"]',
  bar: '[data-cart-bar]',
  ship: '[data-cart-message]',
  itemLoadbar: '[data-item-loadbar]',
  cartMessageContainer: '[data-cart-message-container]',
  apiContent: '[data-api-content]',
};

const classes = {
  hidden: 'cart--hidden',
  loading: 'cart--loading',
};

class CartItems {
  constructor(section) {
    this.section = section;
    this.container = section.container;
    this.cartMessages = document.querySelectorAll(selectors.cartMessageContainer);
    this.bar = this.container.querySelector(selectors.bar);
    this.ship = this.container.querySelector(selectors.ship);
    this.drawer = this.container.querySelector(selectors.drawer);
    this.form = this.container.querySelector(selectors.form);
    this.loader = this.container.querySelector(selectors.loader);
    this.bottom = this.container.querySelector(selectors.bottom);
    this.items = this.container.querySelector(selectors.items);
    this.subtotal = this.container.querySelector(selectors.subtotal);
    this.errors = this.container.querySelector(selectors.errors);
    this.finalPrice = this.container.querySelector(selectors.finalPrice);
    this.emptystate = this.container.querySelector(selectors.emptystate);
    this.progress = this.container.querySelector(selectors.progress);
    this.latestClick = null;
    this.cart = null;
    this.stale = true;
    this.cartPage = document.querySelector(selectors.cartPage);
    this.listen();
  }

  listen() {
    document.addEventListener(
      'theme:cart:change',
      function (event) {
        this.cart = event.detail.cart;
        this.stale = true;
      }.bind(this)
    );

    document.addEventListener(
      'theme:cart:init',
      function () {
        this.init();
      }.bind(this)
    );

    document.addEventListener(
      'theme:cart:reload',
      function () {
        this.stale = true;
        if (this.cart) {
          this.loadHTML();
        } else {
          this.init().then(() => this.loadHTML());
        }
      }.bind(this)
    );

    if (this.drawer) {
      this.drawer.addEventListener(
        'theme:drawer:open',
        function () {
          if (this.cart) {
            this.loadHTML();
          } else {
            this.init().then(() => this.loadHTML());
          }
          // tell the add to cart whether to open a popdown
          window.theme.state.cartOpen = true;
        }.bind(this)
      );
      this.drawer.addEventListener('theme:drawer:close', function () {
        window.theme.state.cartOpen = false;
      });
    }

    new CartNotes(this.container);
    new CartShippingMessage(this.container);
  }

  init() {
    return window
      .fetch(`${window.theme.routes.cart}.js`)
      .then(this.handleErrors)
      .then((response) => {
        return response.json();
      })
      .then((response) => {
        this.cart = response;
        this.fireChange(response);
        return response;
      })
      .catch((e) => {
        console.error(e);
      });
  }

  loadHTML(plain = false) {
    if (this.stale) {
      if (this.cart && this.cart.item_count > 0) {
        this.loadForm(plain);
      } else {
        this.showEmpty();
        this.cartMessages.forEach((message) => {
          new CartShippingMessage(message);
        });
      }
    }
    this.stale = false;
  }

  initInputs() {
    this.inputs = this.container.querySelectorAll(`[${selectors.key}]`);
    this.inputs.forEach((input) => {
      const key = input.getAttribute(selectors.key);
      input.addEventListener(
        'change',
        function (e) {
          const quantity = parseInt(e.target.value, 10);
          this.latestClick = e.target.closest(selectors.item);
          this.lockState();
          this.updateCart(key, quantity);
        }.bind(this)
      );
    });
  }

  initRemove() {
    this.removers = this.container.querySelectorAll(`[${selectors.remove}]`);
    this.removers.forEach((remover) => {
      const key = remover.getAttribute(selectors.remove);
      remover.addEventListener(
        'click',
        function (e) {
          e.preventDefault();
          this.latestClick = e.target.closest(selectors.item);
          this.lockState();
          this.updateCart(key, 0);
        }.bind(this)
      );
    });
  }

  lockState() {
    this.latestClick.querySelector(selectors.itemLoadbar).style.display = 'block';
    this.loader.classList.add(classes.loading);
  }

  updateCart(clickedKey, newQuantity) {
    window
      .fetch(`${window.theme.routes.cart}/change.js`, {
        method: 'post',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({
          id: clickedKey,
          quantity: newQuantity,
        }),
      })
      .then(this.handleErrors)
      .then((response) => {
        return response.json();
      })
      .then((response) => {
        this.cart = response;

        slideUp(this.errors);
        this.fireChange(response);
        this.stale = true;

        this.loadHTML();
      })
      .catch((e) => {
        if (e instanceof FetchError) {
          let defaultMessage = window.theme.strings.stockout || 'Could not update cart, please reload';
          let messageText = e.json?.message || e.message || defaultMessage;
          let descriptionText = e.json?.description || '';

          // Low stock erros have the same text content for heading and description
          let contentToShow = messageText === descriptionText ? `<p>${messageText}</p>` : `<p>${messageText}</p> <p>${descriptionText}</p>`;

          this.showError(contentToShow);
          this.loadForm(); // Reset form for cases like "stockout"
        } else {
          let error = `<p>${e.message || defaultMessage}</p>`;
          this.showError(error);
          throw e;
        }
      });
  }

  fireChange(newCart) {
    document.dispatchEvent(
      new CustomEvent('theme:cart:change', {
        detail: {
          cart: newCart,
        },
        bubbles: true,
      })
    );
  }

  updateTotal() {
    if (this.cart && this.cart.total_price != undefined) {
      const price = formatMoney(this.cart.total_price, theme.moneyFormat);
      this.finalPrice.innerHTML = price + ` ${theme.currencyCode}`;
    }
    if (this.subtotal && this.cart) {
      window
        .fetch(`${window.theme.routes.root_url}?section_id=api-cart-subtotal`)
        .then(this.handleErrors)
        .then((response) => {
          return response.text();
        })
        .then((response) => {
          const fresh = document.createElement('div');
          fresh.innerHTML = response;
          this.subtotal.innerHTML = fresh.querySelector(selectors.apiContent).innerHTML;
        });
    }
  }

  showError(message) {
    slideUp(this.errors);
    this.errors.innerHTML = message;
    window.setTimeout(() => {
      slideDown(this.errors);
    }, 600);
  }

  loadForm(plain = false) {
    if (plain) {
      this.showForm();
      this.initQuantity();
      this.initQuickview();
      this.updateTotal();
      return;
    }

    window
      .fetch(`${window.theme.routes.root_url}?section_id=api-cart-items`)
      .then(this.handleErrors)
      .then((response) => {
        return response.text();
      })
      .then((response) => {
        const fresh = document.createElement('div');
        fresh.innerHTML = response;
        this.items.innerHTML = fresh.querySelector(selectors.apiContent).innerHTML;

        this.showForm();
        this.initQuantity();
        this.initQuickview();
        this.updateTotal();
      });
  }

  initQuickview() {
    const pageUpsellWrapper = this.items.querySelector(selectors.pageUpsellWrapper);
    const oldPageUpsellWrapper = this.bottom.querySelector(selectors.pageUpsellWrapper);

    if (oldPageUpsellWrapper) {
      oldPageUpsellWrapper.remove();
    }

    // Move the upsell to the bottom of the cart beside the checkout button
    if (this.cartPage && pageUpsellWrapper) {
      this.bottom.insertBefore(pageUpsellWrapper, this.bottom.firstChild);
      Alpine.initTree(this.bottom); // Neccesary since we're moving the element around and Alpine isn't able to keep track of tha
    }
  }

  initQuantity() {
    initQtySection(this.container);
    this.initInputs();
    this.initRemove();
  }

  showForm() {
    if (this.bar) {
      this.bar.classList.remove(classes.hidden);
    }
    if (this.ship) {
      this.ship.classList.remove(classes.hidden);
    }
    if (this.progress) {
      this.progress.classList.remove(classes.hidden);
    }
    this.form.classList.remove(classes.hidden);
    this.bottom.classList.remove(classes.hidden);
    this.loader.classList.remove(classes.loading);
    this.emptystate.classList.add(classes.hidden);
  }

  showEmpty() {
    if (this.bar) {
      this.bar.classList.add(classes.hidden);
    }
    if (this.ship) {
      this.ship.classList.add(classes.hidden);
    }
    if (this.progress) {
      this.progress.classList.add(classes.hidden);
    }
    this.emptystate.classList.remove(classes.hidden);
    this.loader.classList.remove(classes.loading);
    this.form.classList.add(classes.hidden);
    this.bottom.classList.add(classes.hidden);
  }

  handleErrors(response) {
    if (!response.ok) {
      return response.json().then(function (json) {
        const e = new FetchError({
          status: response.statusText,
          headers: response.headers,
          json: json,
        });
        throw e;
      });
    }
    return response;
  }
}

const cartDrawer = {
  onLoad() {
    const isDrawerCart = document.querySelector(selectors.drawer);
    if (isDrawerCart) {
      this.cart = new CartItems(this);
    }

    const hasShipping = this.container.querySelector(selectors.shipping);
    if (hasShipping) {
      new ShippingCalculator(this);
    }
  },
  onUnload: function () {
    if (this.cart && typeof this.cart.unload === 'function') {
      this.cart.unload();
    }
  },
};

export {cartDrawer, CartItems};
